import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { filter, map } from "ramda"
import { pipeline } from "../../../../utils/functional"
import type { StateObject } from "../../../redux/types"
import { getSettingsStateObject } from "../selectors"
import type { DeliverySettingsState } from "../reducer"
import { getLocationsAuthData } from "../../../auth/selectors"
import type {
    DeliveryAreaConfig,
    DeliveryFeeConfig,
    DeliveryMapAreaCoords,
    DeliveryPartnersData,
    DeliverySettingsResponse,
    Location,
    Terminal,
} from "../../../../graphql/types"
import { TerminalType } from "../../../../graphql/types"
import { convertCentsToDollars } from "../../../../utils/helpers"
import { getCurrentAgenda } from "../../../router/selectors"
import { MainMenuAgendaEnum } from "../../../router/types"
import { getLocations, getTerminals } from "../../locations/selectors"
import type { SingleChoiceDropdownGroupItem } from "../../../common/components/SingleChoiceDropdown"
import { DEFAULT_DISTANCE_FEE_VALUES } from "./utils"
import { LocationAuthData } from "../../../auth/types"

export const getDeliverySettingsState = (state: StateObject): DeliverySettingsState =>
    getSettingsStateObject(state).deliverySettings

export const getSelectedServiceIdForDeliverySettings = (state: StateObject): Maybe<string> =>
    getDeliverySettingsState(state).selectedServiceId

export const getSelectedServiceDataForDeliverySettings = (state: StateObject): Maybe<Terminal> => {
    const serviceId = getSelectedServiceIdForDeliverySettings(state)
    const terminals = getTerminals(state)
    return terminals.find(t => t.id === serviceId)
}

export const getDeliverySettingsSelectedServiceLocation = (state: StateObject): Maybe<Location> => {
    const serviceId = getSelectedServiceIdForDeliverySettings(state)
    const locations = getLocations(state)
    return serviceId ? locations.find(l => l.terminals.some(t => t.id === serviceId)) : null
}

export const getDeliverySettingsData = (state: StateObject): Maybe<DeliverySettingsResponse> => {
    return getDeliverySettingsState(state).data
}

export const getDeliveryFeeSettingsData = (state: StateObject): Maybe<DeliveryFeeConfig> => {
    const settings = getDeliverySettingsData(state)
    return settings ? settings.deliveryFeeConfig : null
}

export const getDeliveryPartnersSettingsData = (state: StateObject): Maybe<DeliveryPartnersData> => {
    const settings = getDeliverySettingsData(state)
    return settings ? settings.deliveryPartners : null
}

export const getDeliveryAreaSettingsData = (state: StateObject): Maybe<DeliveryAreaConfig> => {
    const settings = getDeliverySettingsData(state)
    return settings ? settings.deliveryAreaConfig : null
}

export const isDeliverySettingsAgendaOpened = (state: StateObject) =>
    getCurrentAgenda(state) === MainMenuAgendaEnum.DeliverySettings

export const getDataForDeliverySettingsServicesDropdown = (state: StateObject): SingleChoiceDropdownGroupItem[] => {
    const applicableTerminalTypes = [TerminalType.Mib, TerminalType.Register, TerminalType.Aggregator]
    // @ts-expect-error
    return pipeline(
        getLocationsAuthData(state),
        filter((l: LocationAuthData) => l.terminals.some(t => applicableTerminalTypes.includes(t.type))),
        map((l: LocationAuthData) => {
            return {
                id: l.id,
                name: l.name,
                children: l.terminals
                    .filter(t => applicableTerminalTypes.includes(t.type))
                    .map(t => ({
                        id: t.id,
                        name: t.name,
                    })),
            }
        })
    )
}

export const getDeliveryFeeFormDataV2 = (state: StateObject): Maybe<DeliveryFeeConfig> => {
    const data = getDeliveryFeeSettingsData(state)

    if (!data) {
        return null
    }

    return {
        ...data,
        baseDeliveryFee: convertCentsToDollars(data.baseDeliveryFee),
        freeDelivery: data.freeDelivery ? convertCentsToDollars(data.freeDelivery) : null,
        distanceFeeValues:
            data.distanceFeeValues && data.distanceFeeValues.length > 0
                ? data.distanceFeeValues.map(dv => ({ ...dv, fee: convertCentsToDollars(dv.fee) || 0 }))
                : DEFAULT_DISTANCE_FEE_VALUES,
    }
}

export const isGeneratingUberDirectId = (state: StateObject): boolean =>
    !!getDeliverySettingsState(state)?.isGeneratingUberId

export const getDeliveryAreaMapCenter = (state: StateObject): Maybe<DeliveryMapAreaCoords> => {
    const activeLocation = getDeliverySettingsSelectedServiceLocation(state)
    return activeLocation && activeLocation.latitude && activeLocation.longitude
        ? {
              lat: activeLocation.latitude,
              lng: activeLocation.longitude,
          }
        : null
}
